import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { imgSrc } from "@/components/Catalog";

export const CartDrawer = () => {
  const { items, cartOpen, setCartOpen, setQty, removeItem, total, setCheckoutOpen } = useCart();

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={() => setCartOpen(false)}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[440px] bg-noir-soft border-l border-white/10 z-50 flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-8 py-6 border-b border-white/10">
              <h2 className="font-serif-lux text-2xl tracking-[0.2em] uppercase text-white flex items-center gap-3">
                <ShoppingBag size={20} className="text-gold" /> Tu cesta
              </h2>
              <button data-testid="cart-close-button" onClick={() => setCartOpen(false)} className="text-neutral-500 hover:text-gold transition-colors" aria-label="Cerrar cesta">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
              {items.length === 0 ? (
                <p className="text-neutral-500 font-light text-center pt-16" data-testid="cart-empty-message">Tu cesta está vacía.</p>
              ) : (
                items.map((item) => (
                  <motion.div key={item.id} layout initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex gap-5" data-testid={`cart-item-${item.id}`}>
                    <div className="w-20 aspect-[3/4] overflow-hidden border border-white/10 shrink-0">
                      <img src={imgSrc(item.image)} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-serif-lux text-lg tracking-[0.1em] uppercase text-white truncate">{item.name}</h3>
                      <p className="text-gold font-serif-lux mt-1">{item.price.toFixed(2)} €</p>
                      <div className="flex items-center gap-3 mt-3">
                        <button data-testid={`cart-qty-minus-${item.id}`} onClick={() => setQty(item.id, item.quantity - 1)} className="w-7 h-7 border border-white/15 flex items-center justify-center text-neutral-400 hover:border-gold hover:text-gold transition-colors" aria-label="Reducir cantidad">
                          <Minus size={12} />
                        </button>
                        <span className="text-sm text-white w-4 text-center">{item.quantity}</span>
                        <button data-testid={`cart-qty-plus-${item.id}`} onClick={() => setQty(item.id, item.quantity + 1)} className="w-7 h-7 border border-white/15 flex items-center justify-center text-neutral-400 hover:border-gold hover:text-gold transition-colors" aria-label="Aumentar cantidad">
                          <Plus size={12} />
                        </button>
                        <button data-testid={`cart-remove-${item.id}`} onClick={() => removeItem(item.id)} className="ml-auto text-neutral-600 hover:text-red-400 transition-colors" aria-label="Eliminar">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 px-8 py-6 space-y-5">
                <div className="flex justify-between items-baseline">
                  <span className="text-[11px] uppercase tracking-[0.3em] text-neutral-400">Total</span>
                  <span className="font-serif-lux text-3xl text-gold" data-testid="cart-total">{total.toFixed(2)} €</span>
                </div>
                <p className="text-[11px] text-neutral-500 font-light">Pago contra reembolso: pagas en efectivo al recibir tu pedido.</p>
                <button
                  data-testid="cart-checkout-button"
                  onClick={() => {
                    setCartOpen(false);
                    setCheckoutOpen(true);
                  }}
                  className="group relative w-full bg-gold text-black text-xs uppercase tracking-[0.3em] py-4 overflow-hidden"
                >
                  <span className="relative z-10 transition-colors duration-500 group-hover:text-gold">Realizar pedido</span>
                  <span className="absolute inset-0 bg-black translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
